import React, { useEffect, useState } from "react";
import { ExternalLink, Sparkles, Award, Star, Users } from "lucide-react";
import TextType from "../ui/TextType";
import PixelCard from "../ui/PixelCard";
import LogoLoop from "../ui/LogoLoop";

const sponsorData = {
  coSponsors: [
    {
      name: "PRD Rigs",
      logo: "https://www.prdrigs.com/img/PRD-logo-02.png",
      description:
        "Leading the future of AI and machine learning solutions for enterprises worldwide.",
      website: "https://www.prdrigs.com/",
    },
    {
      name: "KANINI",
      logo: "https://pub-b5827260d0074856b87ddecd2faaddaa.r2.dev/sponsors/KANI.png",
      description:
        "Building tomorrow's software today with cutting-edge cloud infrastructure.",
      website: "https://kanini.com/",
    },
    {
      name: "Roadmap IT Solutions",
      logo: "https://pub-b5827260d0074856b87ddecd2faaddaa.r2.dev/sponsors/RDMP.jpeg",
      description:
        "Innovative IT consulting and digital transformation services.",
      website: "https://roadmapit.com/",
    },
  ],

  goldSponsors: [
    {
      name: "Career Zone",
      logo: "https://pub-b5827260d0074856b87ddecd2faaddaa.r2.dev/sponsors/careerzonelogo.png",
      website: "https://www.careerzone.in/",
    },
    {
      name: "Emiglitz",
      logo: "https://pub-b5827260d0074856b87ddecd2faaddaa.r2.dev/sponsors/emglitz-logo.webp",
      website: "https://emiglitz.com/",
    },
    {
      name: "Mahindra",
      logo: "https://pub-b5827260d0074856b87ddecd2faaddaa.r2.dev/sponsors/mahindra-auto-logo-png_seeklogo-613492.png",
      website: "https://www.mahindra.com/",
    },
  ],

  associateSponsors: [
    {
      name: "Aishwarya Videos",
      logo: "https://pub-b5827260d0074856b87ddecd2faaddaa.r2.dev/sponsors/AV.jpg",
      website: "#",
    },
    {
      name: "Pugazh Fabrics",
      logo: "https://pub-b5827260d0074856b87ddecd2faaddaa.r2.dev/sponsors/PF.jpeg",
      website: "#",
    },
    {
      name: "Finlatics",
      logo: "https://pub-b5827260d0074856b87ddecd2faaddaa.r2.dev/sponsors/Finlatics%20logo.png",
      website: "https://finlatics.com/",
    },
  ],

  bankingPartner: [
    {
      name: "Indian Bank",
      logo: "https://pub-b5827260d0074856b87ddecd2faaddaa.r2.dev/sponsors/Indian_Bank_logo_2023.png",
      website: "https://www.indianbank.in/",
    },
  ],

  credentialsPartner: [
    {
      name: "TruScholar",
      logo: "https://pub-b5827260d0074856b87ddecd2faaddaa.r2.dev/sponsors/ts%2Bhat%2Bon%2Btop%2Blogo%2Bhat%2Bcolor-min.png",
      website: "",
    },
  ],

  travelPartner: [
    {
      name: "Dare2Roam Holidays",
      logo: "https://pub-b5827260d0074856b87ddecd2faaddaa.r2.dev/sponsors/D2R.jpg",
      website: "https://dare2roamholidays.com/",
    },
  ],

  partners: [
    {
      name: "Waffle Cart",
      logo: "/WC.jpg",
      website: "https://www.zomato.com/coimbatore/waffle-cart-peelamedu",
    },
    {
      name: "Girlush",
      logo: "https://cdn2.zohoecommerce.com/files/Girlush-250x250.png?storefront_domain=www.buygirlush.com",
      website: "https://www.buygirlush.com/",
    },
    { name: "Fat Boy", logo: "/FB.jpg", website: "#" },
  ],
};

const SponsorsPage: React.FC = () => {
  const [visible, setVisible] = useState<Set<string>>(new Set());

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisible((prev) => new Set(prev).add(entry.target.id));
          }
        });
      },
      { threshold: 0.15 }
    );

    document
      .querySelectorAll("[data-animate]")
      .forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const animate = (id: string, delay = "") =>
    `transition-all duration-1000 ${delay} ${
      visible.has(id) ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
    }`;

  return (
    <div
      className="min-h-screen"
      style={{ backgroundColor: "var(--color-background)" }}
    >
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        {/* Hero */}
        <section className="text-center mb-20" data-animate id="hero">
          <div className={animate("hero")}>
            <h1
              className="text-5xl md:text-7xl font-black mb-6 text-transparent bg-clip-text"
              style={{
                backgroundImage:
                  "linear-gradient(to right, var(--color-primary), var(--color-accent))",
              }}
            >
              <TextType
                text={["Powered By Our Sponsors"]}
                typingSpeed={75}
                deletingSpeed={0}
                pauseDuration={1500000}
                showCursor={false}
              />
            </h1>
            <p
              className="text-xl md:text-2xl"
              style={{ color: "var(--color-text-secondary)" }}
            >
              The organizations that elevate ASTRANOVA to the next level
            </p>
          </div>
        </section>

        {/* Co-Sponsors */}
        <section className="mb-32" data-animate id="co-sponsors">
          <div className={animate("co-sponsors", "delay-200")}>
            <Header icon={<Award />} title="Co-Sponsors" />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 justify-items-center">
              {sponsorData.coSponsors.map((s, i) => (
                <a
                  key={i}
                  href={s.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full max-w-sm"
                >
                  <PixelCard variant="darkRed" className="h-full">
                    <div className="flex flex-col items-center p-6 h-full">
                      <img
                        src={s.logo}
                        alt={s.name}
                        className="max-w-xs w-full h-auto mb-6"
                      />
                      <p
                        className="text-center text-sm md:text-base mt-auto pt-6 border-t w-full"
                        style={{ color: "var(--color-text-secondary)" }}
                      >
                        {s.description}
                      </p>
                    </div>
                  </PixelCard>
                </a>
              ))}
            </div>
          </div>
        </section>

        {/* Gold Sponsors */}
        <section className="mb-32" data-animate id="gold-sponsors">
          <div className={`transition-all duration-1000 delay-300`}>
            <div className="flex items-center justify-center gap-3 mb-10">
              <Star
                className="w-8 h-8"
                style={{ color: "var(--color-primary)" }}
              />
              <h2
                className="text-4xl md:text-5xl font-bold"
                style={{ color: "var(--color-primary)" }}
              >
                Gold Sponsors
              </h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8">
              {sponsorData.goldSponsors.map((sponsor, idx) => (
                <a
                  key={idx}
                  href={sponsor.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center justify-center rounded-2xl border-2 transition-all duration-300 hover:scale-105"
                  style={{
                    backgroundColor: "var(--color-surface)",
                    borderColor: "var(--color-primary)",
                    minHeight: "260px", // 🔥 Taller card
                    boxShadow: "0 0 40px rgba(var(--color-primary-rgb), 0.15)",
                  }}
                >
                  <img
                    src={sponsor.logo}
                    alt={sponsor.name}
                    className="w-full max-w-[280px] md:max-w-[320px] max-h-[140px] md:max-h-[170px] object-contain transition-transform duration-300 group-hover:scale-110"
                  />
                </a>
              ))}
            </div>
          </div>
        </section>

        {/* Associate Sponsors */}
        <section className="mb-32" data-animate id="associate-sponsors">
          <div className={animate("associate-sponsors", "delay-400")}>
            <Header icon={<Sparkles />} title="Associate Sponsors" />
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {sponsorData.associateSponsors.map((s, i) => (
                <a
                  key={i}
                  href={s.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rounded-xl p-6 border-2 hover:-translate-y-1 transition"
                  style={{
                    backgroundColor: "var(--color-surface)",
                    borderColor: "var(--color-primary)",
                  }}
                >
                  <img
                    src={s.logo}
                    alt={s.name}
                    className="w-full h-auto object-contain"
                  />
                </a>
              ))}
            </div>
          </div>
        </section>

        {/* Banking & Travel Partners */}
        <section className="mb-32" data-animate id="special-partners">
          <div className={animate("special-partners", "delay-500")}>
            <Header icon={<Users />} title="Our Partners" />
            <div className="grid md:grid-cols-2 gap-8">
              <PartnerBlock
                title="Banking Partner"
                items={sponsorData.bankingPartner}
              />
              <PartnerBlock
                title="Travel Partner"
                items={sponsorData.travelPartner}
              />
              <PartnerBlock
                title="Credentials Partner"
                items={sponsorData.credentialsPartner}
              />
            </div>
          </div>
        </section>

        {/* Scrolling Partners */}
        <section className="mb-32" data-animate id="partners">
          <div className={animate("partners", "delay-600")}>
            <Header icon={<Users />} title="Event Partners" />
            <div
              className="rounded-2xl p-8 border-2"
              style={{
                backgroundColor: "var(--color-surface)",
                borderColor: "var(--color-primary)",
              }}
            >
              <LogoLoop
                logos={sponsorData.partners.map((p) => ({
                  src: p.logo,
                  alt: p.name,
                  href: p.website,
                  title: p.name,
                }))}
                speed={80}
                direction="left"
                logoHeight={48}
                gap={64}
                pauseOnHover
                fadeOut
                fadeOutColor="var(--color-surface)"
                scaleOnHover
                ariaLabel="Partner logos"
              />
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="text-center" data-animate id="cta">
          <div className={animate("cta", "delay-700")}>
            <div
              className="rounded-3xl p-16 border-2"
              style={{
                backgroundColor: "var(--color-surface)",
                borderColor: "var(--color-primary)",
              }}
            >
              <h2
                className="text-4xl md:text-5xl font-bold mb-4"
                style={{ color: "var(--color-primary)" }}
              >
                Want to Sponsor ASTRANOVA?
              </h2>
              <p
                className="text-xl mb-8"
                style={{ color: "var(--color-text-secondary)" }}
              >
                Join us and showcase your brand to thousands of innovators
              </p>
              <a
                href="/contact"
                className="inline-flex items-center gap-2 px-8 py-4 rounded-full font-bold text-white text-lg transition-transform hover:scale-105"
                style={{
                  background:
                    "linear-gradient(to right, var(--color-primary), var(--color-accent))",
                }}
              >
                Become a Sponsor <ExternalLink className="w-5 h-5" />
              </a>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

// ----------------------
// Helpers
// ----------------------
const Header: React.FC<{ title: string; icon: React.ReactNode }> = ({
  title,
  icon,
}) => (
  <div className="flex items-center justify-center gap-3 mb-8">
    <span className="w-7 h-7" style={{ color: "var(--color-primary)" }}>
      {icon}
    </span>
    <h2
      className="text-3xl md:text-4xl font-bold"
      style={{ color: "var(--color-primary)" }}
    >
      {title}
    </h2>
  </div>
);

const PartnerBlock: React.FC<{
  title: string;
  items: { name: string; logo: string; website: string }[];
}> = ({ title, items }) => (
  <div
    className="rounded-2xl p-8 border-2"
    style={{
      backgroundColor: "var(--color-surface)",
      borderColor: "var(--color-primary)",
    }}
  >
    <h3
      className="text-2xl font-semibold mb-6"
      style={{ color: "var(--color-primary)" }}
    >
      {title}
    </h3>
    <div className="flex items-center gap-6">
      {items.map((p, i) => (
        <a key={i} href={p.website} target="_blank" rel="noopener noreferrer">
          <img
            src={p.logo}
            alt={p.name}
            className="h-16 w-auto object-contain"
          />
        </a>
      ))}
    </div>
  </div>
);

export default SponsorsPage;
